import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import activityApi from '../store/api/activityApi';
import { useAppSelector } from './useAppStore';
import { selectIsAuthenticated } from '../store/slices/authSlice';

/** Map URL paths to human-readable page names */
function getPageName(path: string): string {
  if (path.startsWith('/profiles/')) return 'Profile View';
  if (path.startsWith('/messages/')) return 'Messages';
  if (path.startsWith('/profile/setup')) return 'Profile Setup';

  const map: Record<string, string> = {
    '/home':             'Home',
    '/kings-and-queens': 'Kings & Queens',
    '/messages':         'Messages',
    '/subscribe':        'Subscribe',
    '/subscription':     'Subscription',
    '/payment-success':  'Payment Success',
    '/payment-cancel':   'Payment Cancelled',
    '/settings':         'Settings',
    '/profile/me':       'My Profile',
    '/verify':           'Verification',
    '/support':          'Support',
    '/likes':            'Likes',
    '/events':           'Events',
    '/leaderboard':      'Leaderboard',
    '/boosts':           'Boosts',
    '/roses':            'Roses',
  };

  return map[path] ?? path;
}

/** Stable browser-session ID — persists across navigations but not across tabs or restarts */
const SESSION_ID: string = (() => {
  try {
    let id = sessionStorage.getItem('_crays_sid');
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('_crays_sid', id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2);
  }
})();

/**
 * Tracks how long the authenticated user spends on each page.
 * On each route change it fires the previous page's duration to the backend.
 * Mount this once inside the Router context (e.g. in AppInit).
 */
export function usePageTracking() {
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const entryRef = useRef<{ path: string; time: Date } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      entryRef.current = null;
      return;
    }

    const prev = entryRef.current;

    // Flush the previous page's duration before recording the new one
    if (prev) {
      const duration = Math.round((Date.now() - prev.time.getTime()) / 1000);
      if (duration > 0) {
        activityApi.trackPageView({
          page:       prev.path,
          pageName:   getPageName(prev.path),
          duration,
          sessionId:  SESSION_ID,
          enteredAt:  prev.time.toISOString(),
        }).catch(() => { /* fire-and-forget */ });
      }
    }

    // Record the entry time for the current page
    entryRef.current = { path: location.pathname, time: new Date() };

    // Flush on unmount (tab close / logout)
    return () => {
      const current = entryRef.current;
      if (!current) return;
      const duration = Math.round((Date.now() - current.time.getTime()) / 1000);
      if (duration > 0) {
        activityApi.trackPageView({
          page:       current.path,
          pageName:   getPageName(current.path),
          duration,
          sessionId:  SESSION_ID,
          enteredAt:  current.time.toISOString(),
        }).catch(() => { /* fire-and-forget */ });
      }
      entryRef.current = null;
    };
  }, [location.pathname, isAuthenticated]);
}
